/**
 * PujcovnaNastaveniTool — the firma's rental settings on the Správa page (`?nastroj=nastaveni`): the pricing groups and the loyalty
 * threshold (P5-b2, slice-only saves, "Načíst výchozí hodnoty" = the original system's) and the turnaround gap `mezera_dni`, the whole free calendar days required between a car's return and its next pickup
 * (0 = the day after a return, a same-day handover is never allowed). Only new or changed bookings are judged by the
 * new value. Same rules as the other edit surfaces: the form renders only from a read that finished after the page
 * opened (a stale value must not be saved over the real one), its controls freeze while the save is in flight.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Field } from '@/components/Field';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useNastaveniQuery, useSaveNastaveniMutation, type PujcovnaNastaveni } from './api';
import { cislo } from './cenik-helpers';
import { MEZERA_MAX, parseMezera } from './helpers';
import { nastaveniKonflikty, nastaveniPrenes, nastaveniSeed, nastaveniZmena, NASTAVENI_VYCHOZI, type NastaveniForm, type NastaveniValue } from './nastaveni-helpers';

function NastaveniForm({ stored }: { stored: PujcovnaNastaveni }) {
  const save = useSaveNastaveniMutation();
  const [form, setForm] = useState<NastaveniForm>(() => ({ v: nastaveniSeed(stored), base: nastaveniSeed(stored) }));
  const v = form.v;
  const set = (patch: Partial<NastaveniValue>) => setForm((f) => ({ ...f, v: { ...f.v, ...patch } }));
  // the stored values moved under the open form (another admin, or this form's own save): untouched boxes follow, a box being edited keeps
  // its text and is flagged below
  useEffect(() => {
    setForm((f) => nastaveniPrenes(f, nastaveniSeed(stored)));
  }, [stored]);
  const konflikty = nastaveniKonflikty(nastaveniSeed(stored), form);
  const parsedMezera = parseMezera(v.mezera);
  const zmena = nastaveniZmena(stored, v);
  const dirty = zmena.ok && Object.keys(zmena.body).length > 0;
  const busy = save.isPending;
  const cisloChyba = (t: string) => {
    const n = cislo(t);
    return n !== null && Number.isNaN(n);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    // a box that is not a number leaves the change "not ok": the save stays disabled and the box says so
    if (busy || !zmena.ok || !dirty || emailBlokuje) return;
    save.mutate(zmena.body, {
      onSuccess: () => toast.ok('Nastavení uloženo.'),
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit nastavení.'}`),
    });
  };

  // a spend limit is counted over a period: the server refuses one without the other, so say it before the save
  const limitBezObdobi = (cislo(v.limit) ?? 0) > 0 && v.dni.trim() === '';
  // a stored value moved under the open form: the same sentence for every box
  const konfliktHint = (klic: keyof NastaveniValue) =>
    konflikty[klic] === undefined ? undefined : `Hodnota se mezitím změnila (nyní ${konflikty[klic] === '' ? 'nenastaveno' : konflikty[klic]}). Vaše úprava zůstává, uložením ji přepíšete.`;
  const pole = (klic: 's2' | 's3' | 's4' | 'limit' | 'dni', label: string, hint: string, suffix: string, testid: string, chyba = false) => (
    <Field
      label={label}
      optional="volitelné"
      hint={cisloChyba(v[klic]) ? 'Zadejte číslo.' : (konfliktHint(klic) ?? hint)}
      hintTone={cisloChyba(v[klic]) || chyba || konflikty[klic] !== undefined ? 'error' : 'neutral'}
    >
      <TextInput inputMode="decimal" value={v[klic]} suffix={suffix} disabled={busy} data-testid={testid} onChange={(e) => set({ [klic]: e.currentTarget.value })} />
    </Field>
  );

  // the e-mails go out only with a name AND a contact: say it when one is filled and the other is not
  const emailNekompletni = (v.en.trim() !== '') !== (v.ek.trim() !== '');
  // saving a half pair would show 'saved' while the server keeps the e-mails off: the save waits until both are filled (or both cleared)
  const emailBlokuje = emailNekompletni && zmena.ok && ('email_nazev' in zmena.body || 'email_kontakt' in zmena.body);
  const textPole = (klic: 'en' | 'et' | 'ek', label: string, hint: string | undefined, testid: string, maxLength: number, chyba = false) => (
    <Field
      label={label}
      optional={klic === 'et' ? 'volitelné' : undefined}
      hint={konfliktHint(klic) ?? (chyba ? 'Vyplňte název i kontaktní e-mail, jinak se e-maily neodesílají.' : hint)}
      hintTone={konflikty[klic] !== undefined || chyba ? 'error' : 'neutral'}
    >
      <TextInput value={v[klic]} maxLength={maxLength} disabled={busy} data-testid={testid} onChange={(e) => set({ [klic]: e.currentTarget.value })} />
    </Field>
  );

  return (
    <form
      id="pujcovna-nastaveni-form"
      onSubmit={submit}
      data-testid="pujcovna-nastaveni"
      className="flex max-w-[560px] flex-col gap-4 border border-line bg-paper p-5"
    >
      <Field
        label="Mezera mezi rezervacemi (dní)"
        required
        hint={
          parsedMezera == null && v.mezera.trim() !== ''
            ? `Zadejte celé číslo od 0 do ${MEZERA_MAX}.`
            : konflikty.mezera !== undefined
              ? konfliktHint('mezera')
              : '0 = bez odstupu: vozidlo lze znovu půjčit hned po vrácení, i týž den, pokud se termíny nepřekrývají. 1 = mezi vrácením a dalším převzetím musí uplynout 24 hodin, 2 = 48 hodin, a tak dále. Platí pro všechny rezervace vozidel firmy, i technické; stávající rezervace se nemění.'
        }
        hintTone={(parsedMezera == null && v.mezera.trim() !== '') || konflikty.mezera !== undefined ? 'error' : 'neutral'}
      >
        <TextInput
          inputMode="numeric"
          value={v.mezera}
          disabled={busy}
          data-testid="nastaveni-mezera"
          onChange={(e) => set({ mezera: e.currentTarget.value })}
        />
      </Field>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4" data-testid="nastaveni-skupiny">
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">Cenové skupiny klientů</div>
          <p className="mt-1 text-[12px] text-ink-3">
            Host a nový klient platí maximum (100 %). Věrnější klient platí část maxima podle své historie. Cena nikdy neklesne pod minimum vozidla.
          </p>
        </div>
        <Button variant="secondary" size="sm" disabled={busy} data-testid="nastaveni-vychozi" onClick={() => set(NASTAVENI_VYCHOZI)}>
          Načíst výchozí hodnoty
        </Button>
      </div>
      {pole('s2', 'Skupina 2 — klient s jedním pronájmem', 'V % maxima, např. 85.', '%', 'nastaveni-s2')}
      {pole('s3', 'Skupina 3 — více pronájmů, nebo koupil u nás vozidlo', 'V % maxima, např. 80.', '%', 'nastaveni-s3')}
      {pole('s4', 'Skupina 4 — věrný klient podle útraty', 'V % maxima, např. 75.', '%', 'nastaveni-s4')}
      {pole('limit', 'Věrnost: útrata za pronájmy', 'Kolik klient zaplatil za pronájmy (s DPH) v daném období, aby byl ve skupině 4. Prázdné nebo 0 = tato cesta je vypnutá.', 'Kč', 'nastaveni-limit')}
      {pole('dni', 'Věrnost: období', limitBezObdobi ? 'Vyplňte i období — limit útraty se počítá za dané období.' : 'Počet dní zpět, za které se útrata počítá, např. 365.', 'dní', 'nastaveni-dni', limitBezObdobi)}

      <div className="mt-2 border-t border-line pt-4" data-testid="nastaveni-emaily">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">E-maily nájemcům</div>
        <p className="mt-1 text-[12px] text-ink-3">
          Potvrzení platby a zrušení nezaplacené rezervace podepisuje tým této firmy. Odešlou se jen tehdy, je-li vyplněný název i kontaktní e-mail.
        </p>
      </div>
      {textPole('en', 'Název týmu', 'Podpis „tým …“ a jméno odesílatele, např. Půjčovna Auto Trutnov.', 'nastaveni-email-nazev', 100, emailNekompletni && v.en.trim() === '')}
      {textPole('ek', 'Kontaktní e-mail', 'Patička, adresa pro odpověď a kopie každého e-mailu.', 'nastaveni-email-kontakt', 100, emailNekompletni && v.ek.trim() === '')}
      {textPole('et', 'Telefon v patičce', undefined, 'nastaveni-email-telefon', 30)}

      <div className="flex justify-end">
        <Button type="submit" variant="ink" size="sm" disabled={busy || !dirty || emailBlokuje}>
          Uložit
        </Button>
      </div>
    </form>
  );
}

export function PujcovnaNastaveniTool() {
  const q = useNastaveniQuery();
  // A value cached from an earlier visit is not "fresh": the mount-time refetch runs in the background with
  // `isPending` already false, so the form waits for a read that FINISHED after this page mounted.
  const fresh = q.isSuccess && q.isFetchedAfterMount;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
      {q.isError ? (
        <EmptyState eyebrow="Chyba" title="Nastavení se nepodařilo načíst" />
      ) : !fresh ? (
        <Skeleton className="h-32 w-full max-w-[560px]" />
      ) : (
        // a refetch that changed a stored value re-seeds just that box (nastaveniPrenes), so nothing else typed is lost
        <NastaveniForm stored={q.data} />
      )}
    </div>
  );
}
